import * as React from 'react';
import styled from 'styled-components';
import { generatePath } from 'react-router';
import PlaceIcon from '@material-ui/icons/Place';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import CircularProgress from '@material-ui/core/CircularProgress';

import ModalDefaultDraggable from '../molecules/ModalDefaultDraggable';
import { getContactProjects } from '../api/projects';
import { getVocabulariesDataList } from '../api/vocabulary';
import { GET_ALL_QUERY_PARAMS } from '../constants';
import { ROUTES } from '../constants/routes';

type StatusTone = 'success' | 'warning' | 'info' | 'neutral' | 'danger';

const toneByStatus = (raw: string): StatusTone => {
  const s = (raw || '').trim().toLowerCase();
  if (!s) return 'neutral';
  if (s.includes('issued') || s.includes('approved') || s.includes('completed') || s.includes('paid')) return 'success';
  if (s.includes('progress') || s.includes('started') || s.includes('inspection')) return 'info';
  if (s.includes('pending') || s.includes('waiting') || s.includes('sent') || s.includes('hold')) return 'warning';
  if (
    s.includes('denied') ||
    s.includes('rejected') ||
    s.includes('overdue') ||
    s.includes('emergency') ||
    s.includes('failed') ||
    s.includes('withdrawn')
  ) return 'danger';
  return 'neutral';
};

interface Project {
  id: number;
  name: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  jurisdictionName?: string;
  managerName?: string;
  permits: Array<{ id: number; name: string; number?: string; status?: number }>;
}

interface Props {
  userId: number | null;
  onClose: () => void;
}

const UserPermitsModal: React.FC<Props> = ({ userId, onClose }) => {
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [statuses, setStatuses] = React.useState<{ id: number; title: string }[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState('');

  React.useEffect(() => {
    if (!userId) return;
    setLoading(true);
    Promise.all([
      getContactProjects({ ...GET_ALL_QUERY_PARAMS, contact_id: userId } as any),
      getVocabulariesDataList({ ...GET_ALL_QUERY_PARAMS, vocabularyEntity: 6, ordering: 'title' }),
    ])
      .then(([projectsRes, statusesRes]: any) => {
        const raw = projectsRes?.data;
        const list = Array.isArray(raw) ? raw : raw?.results || [];
        setProjects(list);
        setStatuses(statusesRes?.data?.results || []);
      })
      .catch(() => {
        setProjects([]);
        setStatuses([]);
      })
      .finally(() => setLoading(false));
  }, [userId]);

  const statusTitle = (id?: number) => statuses.find(s => s.id === id)?.title || '';

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return projects;
    return projects
      .map(p => {
        const projectMatch =
          p.name?.toLowerCase().includes(q) ||
          p.jurisdictionName?.toLowerCase().includes(q) ||
          p.managerName?.toLowerCase().includes(q) ||
          [p.street, p.city, p.state, p.zipCode].filter(Boolean).join(', ').toLowerCase().includes(q);

        const matchedPermits = (p.permits || []).filter(
          permit =>
            permit.name?.toLowerCase().includes(q) ||
            permit.number?.toLowerCase().includes(q) ||
            statusTitle(permit.status).toLowerCase().includes(q),
        );

        if (projectMatch) return p;
        if (matchedPermits.length) return { ...p, permits: matchedPermits };
        return null;
      })
      .filter(Boolean) as Project[];
  }, [projects, statuses, search]);

  const goToPermit = (projectId: number, permitId: number) => {
    const path = generatePath(ROUTES.PERMIT, { projectId, permitId });
    window.location.assign(path);
  };

  return (
    <StyledModal onClose={onClose} title="User Permits" wide maxHeight="720px">
      <Body>
        <Toolbar>
          <SearchField>
            <SearchIcon style={{ fontSize: 18, color: 'var(--neutral-500)' }} />
            <input
              type="text"
              placeholder="Search projects, permits, status…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <ClearBtn onClick={() => setSearch('')} aria-label="Clear search">
                <CloseIcon style={{ fontSize: 16 }} />
              </ClearBtn>
            )}
          </SearchField>
          <Count>
            {loading ? '' : `${filtered.length} project${filtered.length === 1 ? '' : 's'}`}
          </Count>
        </Toolbar>

        {loading && (
          <CenterState>
            <CircularProgress size={28} />
          </CenterState>
        )}

        {!loading && filtered.length === 0 && (
          <CenterState>No projects or permits found.</CenterState>
        )}

        {!loading &&
          filtered.map(project => {
            const address = [project.street, project.city, project.state, project.zipCode]
              .filter(Boolean)
              .join(', ');
            return (
              <ProjectCard key={project.id}>
                <CardHeader>
                  <HeaderMain>
                    <Eyebrow>Project</Eyebrow>
                    <ProjectName>{project.name || '—'}</ProjectName>
                  </HeaderMain>
                  <HeaderMeta>
                    {address && (
                      <MetaItem>
                        <PlaceIcon style={{ fontSize: 16, color: 'var(--neutral-500)' }} />
                        <span>{address}</span>
                      </MetaItem>
                    )}
                    {project.jurisdictionName && (
                      <MetaItem>
                        <MetaLabel>Jurisdiction:</MetaLabel> {project.jurisdictionName}
                      </MetaItem>
                    )}
                    {project.managerName && (
                      <MetaItem>
                        <MetaLabel>Manager:</MetaLabel> {project.managerName}
                      </MetaItem>
                    )}
                  </HeaderMeta>
                </CardHeader>

                {project.permits?.length ? (
                  <PermitsTable>
                    <PermitsHeader>
                      <span>Permit</span>
                      <span>Number</span>
                      <span>Status</span>
                    </PermitsHeader>
                    {project.permits.map(permit => {
                      const title = statusTitle(permit.status);
                      return (
                        <PermitRow
                          key={permit.id}
                          onClick={() => goToPermit(project.id, permit.id)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') goToPermit(project.id, permit.id);
                          }}
                        >
                          <PermitName>{permit.name || '—'}</PermitName>
                          <PermitNumber>{permit.number || '—'}</PermitNumber>
                          <span>
                            {title ? <StatusChip $tone={toneByStatus(title)}>{title}</StatusChip> : '—'}
                          </span>
                        </PermitRow>
                      );
                    })}
                  </PermitsTable>
                ) : (
                  <NoPermits>No permits on this project.</NoPermits>
                )}
              </ProjectCard>
            );
          })}
      </Body>
    </StyledModal>
  );
};

export default UserPermitsModal;

const StyledModal = styled(ModalDefaultDraggable)``;

const Body = styled.div`
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto,
    sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Toolbar = styled.div`
  position: sticky;
  top: -35px;
  z-index: 1;
  margin: -35px -35px 0;
  padding: 18px 35px 14px;
  background: var(--neutral-0);
  border-bottom: 1px solid var(--border);
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SearchField = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  height: 40px;
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }

  input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.875rem;
    color: var(--mainText);
    font-family: inherit;

    &::placeholder {
      color: var(--neutral-400);
    }
  }
`;

const ClearBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border: none;
  background: var(--neutral-100);
  color: var(--neutral-600);
  border-radius: 999px;
  cursor: pointer;
  padding: 0;

  &:hover {
    background: var(--neutral-200);
    color: var(--neutral-800);
  }
`;

const Count = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-500);
  white-space: nowrap;
`;

const CenterState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  color: var(--neutral-500);
  font-size: 0.875rem;
`;

const ProjectCard = styled.div`
  border: 1px solid var(--border);
  border-radius: 10px;
  background: var(--neutral-0);
  box-shadow: var(--shadow-xs);
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 16px 20px;
  background: var(--neutral-50);
  border-bottom: 1px solid var(--border);
  display: grid;
  grid-template-columns: minmax(220px, 1fr) 2fr;
  gap: 24px;
  align-items: start;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 8px;
  }
`;

const HeaderMain = styled.div`
  min-width: 0;
`;

const Eyebrow = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
  margin-bottom: 4px;
`;

const ProjectName = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--mainText);
  word-break: break-word;
`;

const HeaderMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.875rem;
  color: var(--neutral-700);
  min-width: 0;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  word-break: break-word;
`;

const MetaLabel = styled.span`
  font-weight: 600;
  color: var(--neutral-600);
  margin-right: 4px;
`;

const PermitsTable = styled.div`
  display: flex;
  flex-direction: column;
`;

const PermitsHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 16px;
  padding: 10px 20px;
  background: var(--neutral-100);
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--neutral-500);

  @media (max-width: 600px) {
    display: none;
  }
`;

const PermitRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 16px;
  padding: 12px 20px;
  align-items: center;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: var(--primary-50);
  }

  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px var(--primary-600);
  }

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
    gap: 4px;
  }
`;

const PermitName = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--primary-700);
  word-break: break-word;
`;

const PermitNumber = styled.div`
  font-size: 0.875rem;
  color: var(--mainText);
`;

const NoPermits = styled.div`
  padding: 16px 20px;
  font-size: 0.8125rem;
  color: var(--neutral-500);
`;

const chipPalette: Record<StatusTone, { bg: string; fg: string; border: string }> = {
  success: { bg: 'var(--success-50, #e8f6ee)', fg: 'var(--success-700, #196a3b)', border: 'var(--success-100, #c5e7d2)' },
  warning: { bg: 'var(--warning-50, #fff6e0)', fg: 'var(--warning-700, #8a5f05)', border: 'var(--warning-100, #ffe7ac)' },
  info: { bg: 'var(--info-50, #e8f2fd)', fg: 'var(--info-600, #1e64bf)', border: 'var(--primary-100, #cfe6f3)' },
  danger: { bg: 'var(--danger-50, #fdecec)', fg: 'var(--danger-700, #9a2523)', border: 'var(--danger-100, #f9c9c9)' },
  neutral: { bg: 'var(--neutral-100)', fg: 'var(--neutral-700)', border: 'var(--border)' },
};

const StatusChip = styled.span<{ $tone: StatusTone }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.01em;
  line-height: 1.4;
  white-space: nowrap;
  background: ${({ $tone }) => chipPalette[$tone].bg};
  color: ${({ $tone }) => chipPalette[$tone].fg};
  border: 1px solid ${({ $tone }) => chipPalette[$tone].border};
`;
